/**
 * Promotions listing page
 */
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import AddBoxIcon from '@mui/icons-material/AddBox';

import { ROUTES } from '../../constants/routes';
import Button from '../../components/Button';
import PromotionOverviewFilters from '../../components/PromotionOverviewFilters/PromotionOverviewFilters';
import PromotionListingTable from '../../components/PromotionListingTable/PromotionListingTable';
import {
  defaultPayload,
  constructPayload,
  getPromotionsList,
} from '../../components/PromotionListingTable/promotionapi';
import { Box } from '@mui/material';
import NoData from '../../components/NoData';
import { ShimmerComponent } from '../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../components/Shimmer/BouncingLogo';

const _breadCrumbLinks = [
  {
    url: ROUTES.dashboard,
    title: 'Dashboard',
  },
  {
    url: ROUTES.promotions,
    title: 'Promotions',
  },
];

const style = {
  title: {
    margin: '15px 0 0 0',
    display: 'flex',
    justifyContent: 'space-between',
  },
  button: {
    fontWeight: '600',
    textTransform: 'none',
    marginBottom: '5px',
    marginLeft: '10px',
  },
  icon: {
    fontSize: '14px !important',
  },
  promotionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 20,
  },
};

const Promotions = () => {
  const navigate = useNavigate();
  const apiCalledOnFirstLoad = useRef(false);
  const { isAdmin } = useSelector((state) => state.auth);
  const [promotionsData, setPromotionsData] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const [querySet, setQuerySet] = useState([]);
  const [triggerResetSort, setTriggerResetSort] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(false);
        const promotionsListingData = await getPromotionsList();
        setPromotionsData(promotionsListingData?.data);
        setLoading(false);
      } catch (error) {
        setError(true);
        setLoading(false);
        console.error(error);
      }
    }

    if (!apiCalledOnFirstLoad.current) {
      apiCalledOnFirstLoad.current = true;
      fetchData();
    } else if (Object.keys(selectedFilters).length) {
      const querySet = constructPayload(selectedFilters);
      async function fetchFilteredData() {
        try {
          const promotionsListingData = await getPromotionsList(
            {
              ...defaultPayload,
              query_set: querySet,
            },
            false
          );
          setPromotionsData(promotionsListingData?.data);
          setQuerySet(querySet);
          setLoading(false);
        } catch (error) {
          setError(true);
          setLoading(false);
          console.error(error);
        }
      }
      fetchFilteredData();
    }
  }, [selectedFilters]);

  const handleSelectedFilters = (value) => {
    setSelectedFilters(value);
  };

  const sortReset = () => {
    setTriggerResetSort(!triggerResetSort);
  };

  const handleClearedFilterState = () => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(false);
        setSelectedFilters({});
        setQuerySet([]);
        const promotionsListingData = await getPromotionsList();
        setPromotionsData(promotionsListingData?.data);
        setTriggerResetSort(true);
        setLoading(false);
      } catch (error) {
        setError(true);
        setLoading(false);
        console.error(error);
      }
    }
    fetchData();
  };

  return (
    <Container maxWidth="disable" style={{ height: '88vh', overflow: 'scroll' }}>
      <Grid container spacing={3}>
        <Grid item md={12} lg={12} style={style.promotionsContainer}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography className={'promotions-overview-title'} style={style.title} variant={'h5'}>
              Promotions Overview
              <div>
                {isAdmin ? (
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    style={style.button}
                    startIcon={<AddBoxIcon style={style.icon} />}
                    onClick={() => navigate(ROUTES.createPromotions)}
                  >
                    Create Template
                  </Button>
                ) : null}
              </div>
            </Typography>
          </Grid>
          <Grid container style={{ marginBottom: 20 }}>
            <Grid
              item
              container
              justifyContent="end"
              style={{ marginBottom: 20 }}
              className="offer-listing"
            >
              <PromotionOverviewFilters
                handleSelectedFilters={handleSelectedFilters}
                handleClearedFilterState={handleClearedFilterState}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <div style={{ marginBottom: '60px' }}>
        {loading && (
          <ShimmerComponent
            updatedStyle={{
              width: '100%',
              height: '100%',
              backgroundSize: '2000px 800px',
              border: '0px',
              margin: 'auto',
              padding: '0 20px',
            }}
          >
            <BouncingLogo height="72vh" text="Loading..." />
          </ShimmerComponent>
        )}

        {error && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <NoData textValue="Error loading data" />
          </Box>
        )}

        {!loading && !error ? (
          <PromotionListingTable
            isAdmin={isAdmin}
            defaultPromotionsData={promotionsData?.data}
            totalPages={promotionsData?.total_pages}
            querySet={querySet}
            triggerResetSort={triggerResetSort}
            sortReset={sortReset}
          />
        ) : null}
      </div>
    </Container>
  );
};

export default Promotions;
